import { Trans, useLingui } from '@lingui/react/macro'
import faceImage from '$src/assets/images/face.png'
import {
  Button,
  Group,
  Image,
  Paper,
  rem,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react'
import { authHooks } from '../../lib/trpcHooks'

interface EmailStepProps {
  turnstileRef: (node: HTMLDivElement | null) => void
  turnstileToken: string
  resetTurnstile: () => void
  setTurnstileToken: (token: string) => void
  onBack: () => void
  onSuccess: (email: string) => void
}

export function EmailStep({
  turnstileRef,
  turnstileToken,
  resetTurnstile,
  setTurnstileToken,
  onBack,
  onSuccess,
}: EmailStepProps) {
  const { t } = useLingui()

  const form = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) =>
        /^\S+@\S+$/.test(value) ? null : t`Invalid email address`,
    },
  })

  const requestOtp = authHooks.useRequestOtp()

  const handleContinue = async () => {
    const validation = form.validate()
    if (validation.hasErrors) return

    try {
      await requestOtp.mutateAsync({
        email: form.values.email,
        turnstileToken,
      })
      notifications.show({
        title: t`Code sent!`,
        message: t`Check your email ${form.values.email}`,
        color: 'green',
      })
      onSuccess(form.values.email)
    } catch {
      // Error handled by trpcHooks
      // Reset Turnstile widget on error
      resetTurnstile()
      setTurnstileToken('')
    }
  }

  return (
    <Paper
      radius="xl"
      p="xl"
      withBorder
      shadow="xl"
      style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        width: '100%',
      }}
    >
      <Stack>
        <Group justify="center" mb="md">
          <Image
            src={faceImage}
            alt="Markku"
            style={{
              width: rem(80),
              height: rem(80),
              objectFit: 'contain',
            }}
          />
        </Group>

        <Title order={3} ta="center" mb="xs">
          <Trans>Enter your email address</Trans>
        </Title>
        <Text c="dimmed" ta="center" mb="lg">
          <Trans>We will send a login code to your email</Trans>
        </Text>

        <TextInput
          type="email"
          label={t`Email`}
          name="username"
          autoComplete="home email"
          placeholder="name@example.com"
          {...form.getInputProps('email')}
          radius="md"
          size="lg"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !form.errors.email && turnstileToken) {
              handleContinue()
            }
          }}
        />

        {/* Turnstile widget container */}
        <div
          ref={turnstileRef}
          style={{ display: 'flex', justifyContent: 'center' }}
        />

        <Group justify="space-between" mt="xl">
          <Button
            size="lg"
            radius="xl"
            variant="subtle"
            onClick={onBack}
            leftSection={<IconArrowLeft size={20} />}
          >
            <Trans>Back</Trans>
          </Button>
          <Button
            size="lg"
            radius="xl"
            color="brandOrange"
            onClick={handleContinue}
            loading={requestOtp.isPending}
            disabled={
              !form.values.email || !!form.errors.email || !turnstileToken
            }
            rightSection={<IconArrowRight size={20} />}
          >
            <Trans>Continue</Trans>
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}
